import React, { useState, useEffect, useRef } from 'react'
import {
  TopPageLink,
  Reviews,
  ReviewsButtonModal,
  ImagePreview
} from '../../../components'
import moment from 'moment'
import userImg from '../../../assets/user.svg'
import {
  PauseCircleOutlined,
  ReloadOutlined,
  DownloadOutlined,
  LoadingOutlined,
  SearchOutlined,
  InfoCircleOutlined,
  CommentOutlined,
  PaperClipOutlined,
  ClockCircleOutlined,
  PlayCircleOutlined
} from '@ant-design/icons'
import { Spin, Alert, Rate, message, Tooltip } from 'antd'
import { api } from '../../../services'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import './DiscussionListen.scss'
import AudioPlayer from './AudioPlayer'
import {
  addReviewsRequest,
  getReviewsRequest
} from '../../../store/actions/reviews'
import fileSaver from 'file-saver'

/* ─────────────────────────────────────────────────────────────
   GUEST HEADER
───────────────────────────────────────────────────────────── */
const GuestHeader = ({ t, podcastHost, avgRating, eventId, userId }) => (
  <div className="dl-guest-bar">
    <div className="dl-guest-left">
      <p className="dl-eyebrow">{t('v3:podcastGuest')}</p>
      <div className="dl-avatars">
        {podcastHost.map((host, i) => (
          <Tooltip key={i} title={`${host.first_name} ${host.last_name}`}>
            <div className="dl-avatar">
              <img
                src={host.avatar ? host.avatar.link : userImg}
                alt={host.user_name}
              />
            </div>
          </Tooltip>
        ))}
      </div>
    </div>
    <ReviewsButtonModal
      avgRating={avgRating}
      id={eventId}
      userId={userId}
      podcast
      type="event"
    />
  </div>
)

/* ─────────────────────────────────────────────────────────────
   HERO + AUDIO PLAYER
───────────────────────────────────────────────────────────── */
const NowPlayingHero = ({
  cover,
  audioLink,
  audioName,
  playPauseIndex,
  onPlayPause,
  autoPlayNext,
  isDownLoadingId
}) => (
  <div className="dl-hero">
    <div
      className="dl-hero-cover-wrap"
      style={{
        '--cover-bg': cover
          ? `url(${cover})`
          : 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)'
      }}
    >
      <div className="dl-hero-bg-blur" />
      <div className="dl-hero-inner">
        {cover ? (
          <div className="dl-hero-img-box">
            <ImagePreview src={cover} alt="Cover" className="dl-hero-img" />
          </div>
        ) : (
          <div className="dl-hero-placeholder">
            <div className="dl-audio-waves">
              <div className="dl-audio-bar" />
              <div className="dl-audio-bar" />
              <div className="dl-audio-bar" />
              <div className="dl-audio-bar" />
              <div className="dl-audio-bar" />
            </div>
          </div>
        )}
      </div>
      {audioLink && (
        <div className="dl-player-wrap">
          <AudioPlayer
            audioName={audioName}
            playPauseIndex={playPauseIndex}
            audioUrl={audioLink}
            onPlayPause={onPlayPause}
            autoPlayNext={autoPlayNext}
          />
        </div>
      )}
    </div>
  </div>
)

/* ─────────────────────────────────────────────────────────────
   INFO TABS
───────────────────────────────────────────────────────────── */
const TAB_ICONS = {
  OVERVIEW: <InfoCircleOutlined />,
  REVIEW: <CommentOutlined />,
  ATTACHMENT: <PaperClipOutlined />
}

const InfoTabs = ({
  t,
  tab,
  setActiveTab,
  podcastData,
  attachedData,
  isDownLoading,
  downloadFile,
  reviews,
  hasReviewed,
  reviewText,
  onReviewInput,
  sendReview,
  isReviewLoading,
  eventId,
  userId
}) => {
  const tabs = [
    { key: 'OVERVIEW', label: t('tabs:overview') },
    { key: 'REVIEW', label: t('v4:reviews') }
  ]
  if (attachedData.attachmentIds.length > 0) {
    tabs.push({ key: 'ATTACHMENT', label: t('v4:tab_attachments') })
  }

  return (
    <div className="dl-info-card">
      <nav className="dl-tab-nav">
        {tabs.map(({ key, label }) => (
          <button
            key={key}
            className={`dl-tab-btn${tab === key ? ' dl-tab-btn--active' : ''}`}
            onClick={() => setActiveTab(key)}
          >
            {TAB_ICONS[key]}
            <span>{label}</span>
          </button>
        ))}
      </nav>

      <div className="dl-tab-body">
        {tab === 'OVERVIEW' && (
          <div className="dl-overview">
            <h1 className="dl-overview-title">{podcastData.title}</h1>
            <div className="dl-overview-meta">
              <ClockCircleOutlined />
              <span>
                {podcastData.discussion_room_type === 'live'
                  ? moment(podcastData.start_date_time).format('LLLL')
                  : moment(podcastData.created_at).format('LLLL')}
              </span>
            </div>
            <div
              className="dl-overview-desc ql-editor"
              dangerouslySetInnerHTML={{
                __html: podcastData.description || 'No description available.'
              }}
            />
          </div>
        )}

        {tab === 'ATTACHMENT' && (
          <ul className="dl-attach-list">
            {attachedData.filenames.map((name, i) => (
              <li key={i} className="dl-attach-item">
                <div className="dl-attach-icon">
                  <PaperClipOutlined />
                </div>
                <span className="dl-attach-name">{name}</span>
                <button
                  className="dl-attach-dl"
                  onClick={() =>
                    downloadFile(attachedData.attachmentIds[i], name)
                  }
                >
                  {isDownLoading.status &&
                  isDownLoading.id === attachedData.attachmentIds[i] ? (
                    <ReloadOutlined spin />
                  ) : (
                    <DownloadOutlined />
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}

        {tab === 'REVIEW' && (
          <div className="dl-review-tab">
            {!hasReviewed && (
              <div className="dl-review-row">
                <input
                  className="dl-review-input"
                  placeholder="Share your thoughts on this episode…"
                  value={reviewText.text}
                  onChange={onReviewInput}
                />
                <button
                  className="dl-review-send"
                  onClick={sendReview}
                  disabled={isReviewLoading}
                >
                  {isReviewLoading ? (
                    <LoadingOutlined spin />
                  ) : (
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      width="14"
                      height="14"
                    >
                      <line x1="22" y1="2" x2="11" y2="13" />
                      <polygon points="22 2 15 22 11 13 2 9 22 2" />
                    </svg>
                  )}
                </button>
              </div>
            )}
            {reviews.length > 0 ? (
              <Reviews id={eventId} userId={userId} type="event" podcast />
            ) : (
              <p className="dl-review-empty">
                No reviews yet. Be the first to share your thoughts.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

/* ─────────────────────────────────────────────────────────────
   PLAYBACK QUEUE
───────────────────────────────────────────────────────────── */
const PlaybackQueue = ({
  t,
  podcastAudio,
  searchText,
  handleSearch,
  isDownLoading,
  playPauseStatus,
  handleSelectAudio
}) => (
  <div className="dl-queue-card">
    <div className="dl-queue-header">
      <h3 className="dl-queue-title">{t('v3:podcastEpishode')}</h3>
      <span className="dl-queue-count">{podcastAudio.list.length}</span>
    </div>
    <div className="dl-queue-search">
      <SearchOutlined className="dl-queue-icon" />
      <input
        className="dl-queue-input"
        placeholder={t('v2:search')}
        value={searchText}
        onChange={handleSearch}
      />
    </div>
    <div className="dl-queue-body">
      <Spin spinning={podcastAudio.status}>
        {podcastAudio.list.length === 0 ? (
          <div className="dl-queue-empty">
            {!podcastAudio.status && (
              <>
                <InfoCircleOutlined className="dl-queue-empty-icon" />
                <p>
                  {searchText
                    ? 'No episodes match your search.'
                    : 'No episodes available.'}
                </p>
              </>
            )}
          </div>
        ) : (
          <ul className="dl-track-list">
            {podcastAudio.list.map((audio, i) => {
              const isActive = playPauseStatus.id === audio.id
              return (
                <li
                  key={i}
                  className={`dl-track${isActive ? ' dl-track--active' : ''}`}
                  onClick={() => handleSelectAudio(audio)}
                >
                  <span className="dl-track-num">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <div className="dl-track-info">
                    <span className="dl-track-name">{audio.title}</span>
                    <span className="dl-track-dur">
                      <ClockCircleOutlined /> {audio.duration}
                    </span>
                  </div>
                  <span className="dl-track-action">
                    {isDownLoading.status && isDownLoading.id === audio.id ? (
                      <ReloadOutlined spin />
                    ) : isActive && !playPauseStatus.isPaused ? (
                      <PauseCircleOutlined />
                    ) : (
                      <PlayCircleOutlined />
                    )}
                  </span>
                </li>
              )
            })}
          </ul>
        )}
      </Spin>
    </div>
  </div>
)

/* ─────────────────────────────────────────────────────────────
   RATING MODAL
───────────────────────────────────────────────────────────── */
const RatingModal = ({
  visible,
  reviewText,
  onClose,
  onSave,
  isLoading,
  onStarChange
}) => {
  if (!visible) return null
  return (
    <div
      className="dl-modal-overlay"
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div className="dl-modal">
        <button className="dl-modal-close" onClick={onClose}>
          ×
        </button>
        <h2 className="dl-modal-title">How would you rate this?</h2>
        {reviewText.text && (
          <p className="dl-modal-quote">"{reviewText.text}"</p>
        )}
        <Rate
          className="dl-modal-stars"
          value={reviewText.value}
          onChange={onStarChange}
        />
        <button
          className="dl-modal-submit"
          onClick={onSave}
          disabled={isLoading}
        >
          {isLoading ? <LoadingOutlined spin /> : 'Submit rating'}
        </button>
      </div>
    </div>
  )
}

/* ─────────────────────────────────────────────────────────────
   MAIN
───────────────────────────────────────────────────────────── */
const DiscussionListen = ({ match, user, location }) => {
  const { t } = useTranslation()
  const reviews = useSelector(state => state.reviews.reviews)
  const avgRating = useSelector(state => state.reviews.avg_rating)
  const dispatch = useDispatch()

  const [userId] = useState(user.info.id)
  const [eventId] = useState(match.params.id)
  const [isLoading, setIsLoading] = useState(false)
  const [currentPlayIndex, setCurrentPlayIndex] = useState(0)
  const currentPlayIndexRef = useRef(0)
  const [playPauseIndex, setPlayPauseIndex] = useState(0)
  const [isDownLoading, setIsDownLoading] = useState({
    status: false,
    id: '',
    title: ''
  })
  const [hasReviewed, setHasReviewed] = useState(true)
  const [isReviewLoading, setIsReviewLoading] = useState(false)
  const [playPauseStatus, setPlayPauseStatus] = useState({
    isPaused: true,
    id: ''
  })
  const [isError, setIsError] = useState({ status: false, message: '' })
  const [audioLink, setAudioLink] = useState('')
  const [reviewText, setReviewText] = useState({ text: '', value: null })
  const [reviewStarModal, setReviewStarModal] = useState(false)
  const [searchText, setSearchText] = useState('')
  const [tab, setActiveTab] = useState('OVERVIEW')
  const [podcastHost, setPodcastHost] = useState([])
  const [podcastAudio, setPodcastAudio] = useState({
    list: [],
    allList: [],
    status: false
  })
  const [podcastList, setPodcastList] = useState([])
  const [podcastData, setPodcastData] = useState({
    cover: { link: '' },
    id: '',
    title: '',
    start_date_time: null,
    created_at: null,
    description: '',
    avg_rating: null
  })
  const [attachedData, setAttachedData] = useState({
    filenames: [],
    attachmentIds: []
  })

  useEffect(() => {
    getEventById(eventId)
    getRating(eventId)
  }, [])

  useEffect(() => {
    setPodcastAudio(prev => ({
      ...prev,
      list: prev.allList.filter(el =>
        el.title.toLowerCase().includes(searchText.toLowerCase())
      )
    }))
  }, [searchText])

  useEffect(() => {
    if (reviews?.length > 0)
      setHasReviewed(reviews.some(r => r.user_id === userId))
    else setHasReviewed(false)
  }, [reviews])

  const handleSearch = e => setSearchText(e.target.value)

  const getEventById = async (id, isRefresh) => {
    setIsLoading(!isRefresh)
    try {
      const { data } = await api.events.getById(id)
      setPodcastData({
        cover: data.cover,
        created_at: data.created_at,
        id: data.id,
        title: data.title,
        description: data.description,
        avg_rating: data.avg_rating
      })
      getAttachmentDetail(data.attachments)
      if (data.hosts) setPodcastHost(data.hosts)
      if (data.audio?.[0]) setAudioData(data.audio)
    } catch (error) {
      const text =
        error.response?.data?.errorMessage || 'Failed to load dashboard'
      message.error(text)
      setIsError({
        status: true,
        message: text,
        type: error.response?.data?.errorType || 'Not Found'
      })
    } finally {
      setIsLoading(false)
    }
  }

  const getAttachmentDetail = async ids => {
    if (!ids?.length) return
    const names = await Promise.all(
      ids.map(id => api.library.getMediaInfoById(id).then(r => r.data.filename))
    )
    setAttachedData({ attachmentIds: ids, filenames: names })
  }

  const getRating = id => dispatch(getReviewsRequest(id, 'event'))
  const onReviewInput = e =>
    setReviewText(prev => ({ ...prev, text: e.target.value }))
  const sendReview = () =>
    reviewText.text.length > 0 && setReviewStarModal(true)

  const saveReview = async () => {
    setIsReviewLoading(true)
    await dispatch(
      addReviewsRequest(eventId, 'event', {
        review: reviewText.text,
        rating: reviewText.value
      })
    )
    setReviewText({ text: '', value: null })
    setIsReviewLoading(false)
    setReviewStarModal(false)
  }

  const handleSelectAudio = audio => {
    if (audio.id === isDownLoading.id) return
    const idx = podcastList.findIndex(el => el.id === audio.id)
    setCurrentPlayIndex(idx)
    currentPlayIndexRef.current = idx
    setPlayPauseIndex(0)
    setPodcastData(prev => ({
      ...prev,
      cover: audio.cover,
      title: audio.title,
      description: audio.description
    }))
    setAudioLink(audio.downloadble_link)
    setIsDownLoading({ status: true, id: audio.id, title: audio.title })
    setTimeout(() => {
      setPlayPauseStatus({ isPaused: false, id: audio.id })
      setIsDownLoading({ status: false, id: audio.id, title: audio.title })
    }, 300)
  }

  const autoPlayNext = () => {
    const next = currentPlayIndexRef.current + 1
    if (next < podcastList.length) handleSelectAudio(podcastList[next])
    else setPlayPauseStatus(prev => ({ ...prev, isPaused: true }))
  }

  const formatTime = s =>
    `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`

  const setAudioData = async data => {
    setPodcastAudio({ list: [], allList: [], status: true })
    const audioList = await Promise.all(
      data.map(async audio => {
        try {
          const { data: dl } = await api.library.download({ id: audio.id })
          const duration = await new Promise((res, rej) => {
            const a = new Audio(dl.downloadble_link)
            a.addEventListener('loadedmetadata', () =>
              res(formatTime(a.duration))
            )
            a.addEventListener('error', rej)
          })
          return { ...audio, duration, downloadble_link: dl.downloadble_link }
        } catch {
          return { ...audio, duration: 0, downloadble_link: '' }
        }
      })
    )
    setPodcastAudio({ list: audioList, allList: audioList, status: false })
    setPodcastList(audioList)
  }

  const downloadFile = (id, name) => {
    setIsDownLoading(prev => ({ ...prev, status: true, id }))
    api.library
      .download({ id })
      .then(({ data }) => fileSaver.saveAs(data.downloadble_link, name))
      .catch(() => message.error('Download failed'))
      .finally(() =>
        setIsDownLoading(prev => ({ ...prev, status: false, id: '' }))
      )
  }

  const skillJourneyId = location.state?.skillJourneyId
  const skillJourney = location.state?.skillJourney

  return (
    <div className="dl-root">
      {/* TopPageLink — unchanged per requirement */}
      <TopPageLink
        page={skillJourney ? t('v4:skill') : t('navigation:training')}
        path={skillJourney ? `/skill-journey/${skillJourneyId}` : '/training'}
      />

      <RatingModal
        visible={reviewStarModal}
        reviewText={reviewText}
        onClose={() => setReviewStarModal(false)}
        onSave={saveReview}
        isLoading={isReviewLoading}
        onStarChange={v => setReviewText(prev => ({ ...prev, value: v }))}
      />

      {isLoading ? (
        <div className="dl-page-loader">
          <Spin size="large" />
        </div>
      ) : isError.status ? (
        <div className="dl-error">
          <Alert
            showIcon
            message={isError.type}
            description={isError.message}
            type="error"
          />
        </div>
      ) : (
        <div className="dl-layout">
          <main className="dl-main">
            <GuestHeader
              t={t}
              podcastHost={podcastHost}
              avgRating={avgRating}
              eventId={eventId}
              userId={userId}
            />
            <NowPlayingHero
              cover={podcastData.cover?.link}
              audioLink={audioLink}
              audioName={isDownLoading.title}
              playPauseIndex={playPauseIndex}
              onPlayPause={isp =>
                setPlayPauseStatus({ isPaused: isp, id: isDownLoading.id })
              }
              autoPlayNext={autoPlayNext}
              isDownLoadingId={!!audioLink}
            />
            <InfoTabs
              t={t}
              tab={tab}
              setActiveTab={setActiveTab}
              podcastData={podcastData}
              attachedData={attachedData}
              isDownLoading={isDownLoading}
              downloadFile={downloadFile}
              reviews={reviews}
              hasReviewed={hasReviewed}
              reviewText={reviewText}
              onReviewInput={onReviewInput}
              sendReview={sendReview}
              isReviewLoading={isReviewLoading}
              eventId={eventId}
              userId={userId}
            />
          </main>
          <aside className="dl-sidebar">
            <PlaybackQueue
              t={t}
              podcastAudio={podcastAudio}
              searchText={searchText}
              handleSearch={handleSearch}
              isDownLoading={isDownLoading}
              playPauseStatus={playPauseStatus}
              handleSelectAudio={handleSelectAudio}
            />
          </aside>
        </div>
      )}
    </div>
  )
}

export default DiscussionListen
